import { useEffect, type RefObject } from 'react';
import { TABLE_KEY } from './Cmarkdowntable.constants';
import type {
  CmarkdowntablePopoverEntry,
  CmarkdowntablePopoverRect,
} from './Cmarkdowntable.types';

interface UseCmarkdowntablePopoverEffectsInput {
  activeRowIndex: number | null;
  popoverEntries: CmarkdowntablePopoverEntry[] | null;
  activeAnchorRef: RefObject<HTMLDivElement | null>;
  popoverRef: RefObject<HTMLDivElement | null>;
  manualRectRef: RefObject<CmarkdowntablePopoverRect | null>;
  updatePopoverPosition: () => void;
  clampManualRectToViewport: () => void;
  closePopover: () => void;
}

export function useCmarkdowntablePopoverEffects(
  input: UseCmarkdowntablePopoverEffectsInput,
): void {
  const {
    activeRowIndex,
    popoverEntries,
    activeAnchorRef,
    popoverRef,
    manualRectRef,
    updatePopoverPosition,
    clampManualRectToViewport,
    closePopover,
  } = input;

  useEffect(() => {
    if (popoverEntries == null || activeRowIndex == null) {
      return;
    }

    const frameId = window.requestAnimationFrame(updatePopoverPosition);
    const handleScroll = (event: Event): void => {
      const eventTarget = event.target;
      if (eventTarget instanceof Node && popoverRef.current?.contains(eventTarget)) {
        return;
      }

      updatePopoverPosition();
    };

    const handleViewportResize = (): void => {
      if (manualRectRef.current) {
        clampManualRectToViewport();
        return;
      }

      updatePopoverPosition();
    };

    window.addEventListener('resize', handleViewportResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleViewportResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [
    activeRowIndex,
    popoverEntries,
    popoverRef,
    manualRectRef,
    updatePopoverPosition,
    clampManualRectToViewport,
  ]);

  useEffect(() => {
    if (activeRowIndex == null) {
      return;
    }

    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node | null;
      if (!target) {
        return;
      }

      if (activeAnchorRef.current?.contains(target) || popoverRef.current?.contains(target)) {
        return;
      }

      closePopover();
    };

    const handleEscapePress = (event: globalThis.KeyboardEvent): void => {
      if (event.key === TABLE_KEY.Escape) {
        closePopover();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapePress);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapePress);
    };
  }, [activeRowIndex, activeAnchorRef, popoverRef, closePopover]);
}
